/**
 * "Prepare for a meeting" — the setup sheet (2026-09-24, Andrew and Colin).
 *
 * Asks only what the model cannot know: how and when, who the rep expects to see, and
 * what THEY want out of it. Everything else comes from the account. Earlier plans for
 * the same account are listed first — the last plan is where the next one starts.
 *
 * `subject` = {entity, code, name, contacts?, workItem?}  for an account (a QX `#` prospect included)
 *             `workItem` = {id, title, kind} preselects the deal (opened from an opportunity)
 *           = {entity, leadId, name}           for a lead with no account yet
 */
import { escapeHtml as esc, getIcon, localInputToISO, router, showError, showModal } from '@isg/ui-kit';
import { salesApi } from '../services/sales-api.js';
import { saving, segment, wireSegment } from './log-sheet.js';

const KINDS = [['visit', 'Visit', 'map-pin'], ['call', 'Call', 'phone'], ['video', 'Video call', 'video']];

function fmt(iso) {
  if (!iso) return 'no date set';
  return new Date(iso).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function openMeetingSetup(subject) {
  const state = { kind: 'visit', attendees: [] };
  const people = (subject.contacts || []).filter(c => c.name).slice(0, 10);
  const content = document.createElement('div');
  content.innerHTML = `
    <p style="margin:0 0 var(--space-3);color:var(--text-secondary);font-size:var(--text-sm)">
      A plan for <strong>${esc(subject.name || subject.code || 'this prospect')}</strong>, written from what we know about
      ${subject.leadId ? 'this lead' : 'the account'} — trade, quotes, orders, contacts, what your team has logged and
      what they have been doing online — plus ideas nobody has tried yet. You can edit it, talk it through, print it or email it.</p>
    <div data-previous></div>
    <div class="form-group"><label class="form-label">How</label>${segment('kind', KINDS, 'visit')}</div>
    <div class="form-group"><label class="form-label" for="mp-when">When</label>
      <input class="form-input" type="datetime-local" id="mp-when" style="max-width:260px"></div>
    <div class="form-group"><label class="form-label" for="mp-who">Who you expect to see</label>
      <input class="form-input" id="mp-who" maxlength="600" placeholder="Names or roles, separated by commas">
      ${people.length ? `<div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:6px">${people.map(p =>
        `<button type="button" class="badge badge-gray" style="cursor:pointer;border:0" data-person="${esc(p.name)}"
          title="${esc(p.job_title || '')}">${getIcon('plus', 10)} ${esc(p.name)}</button>`).join('')}</div>` : ''}</div>
    ${subject.code ? `<div class="form-group"><label class="form-label" for="mp-deal">Is it about a deal?</label>
      <select class="form-select" id="mp-deal"><option value="">No — the account in general</option>
        ${subject.workItem ? `<option value="${esc(subject.workItem.id)}" selected>${esc(subject.workItem.title)}</option>` : ''}</select>
      <div style="font-size:12px;color:var(--text-tertiary);margin-top:4px">Link an opportunity or project and the plan is written around moving it forward — its parts, its quotes, its stage.</div></div>` : ''}
    <div class="form-group"><label class="form-label" for="mp-aim">What do you want out of it?</label>
      <textarea class="form-textarea" id="mp-aim" rows="3" maxlength="2000"
        placeholder="e.g. find out why they stopped ordering the M81714 contacts and get a quote request"></textarea>
      <div style="font-size:12px;color:var(--text-tertiary);margin-top:4px">Optional — but the plan is sharper with it.</div></div>`;

  const modal = showModal({
    title: 'Prepare for a meeting', content, size: 'md',
    actions: [
      { label: 'Cancel', className: 'btn btn-secondary' },
      { label: 'Write the plan', className: 'btn btn-primary', onClick: (close) => create(close) },
    ],
  });
  wireSegment(content, 'kind', v => { state.kind = v || 'visit'; });
  const who = content.querySelector('#mp-who');
  content.querySelectorAll('[data-person]').forEach(b => b.addEventListener('click', () => {
    const names = who.value.split(',').map(x => x.trim()).filter(Boolean);
    if (!names.includes(b.dataset.person)) names.push(b.dataset.person);
    who.value = names.join(', ');
  }));

  // the account's live deals for the picker (both kinds; the one we came from stays selected)
  if (subject.code) {
    Promise.all(['opportunity', 'project'].map(k => salesApi.getWorkItems(k,
      { entity: subject.entity, customer: subject.code, stage: 'open', limit: 50 }).catch(() => null)))
      .then(([opps, projs]) => {
        const sel = content.querySelector('#mp-deal');
        if (!sel) return;
        const items = [...((opps && opps.data && opps.data.items) || []), ...((projs && projs.data && projs.data.items) || [])]
          .filter(it => !subject.workItem || it.id !== subject.workItem.id);
        sel.insertAdjacentHTML('beforeend', items.map(it =>
          `<option value="${esc(it.id)}">${it.kind === 'project' ? 'Project' : 'Opportunity'}: ${esc(it.title)} (${esc(it.stage)})</option>`).join(''));
      });
  }

  // earlier plans for the same account or lead
  const params = subject.leadId ? { entity: subject.entity, lead: subject.leadId } : { entity: subject.entity, customer: subject.code };
  salesApi.listMeetingPlans(params).then(res => {
    const rows = (res && res.data) || [];
    if (!rows.length) return;
    content.querySelector('[data-previous]').innerHTML = `<div class="form-group">
      <label class="form-label">Earlier plans</label>
      <div style="display:flex;flex-direction:column;gap:4px">${rows.slice(0, 5).map(r => `
        <a href="/meeting-plans/${esc(r.id)}" style="font-size:var(--text-sm)">
          ${getIcon('file-text', 12)} ${esc(fmt(r.meeting_at))} · ${esc(r.meeting_kind)}
          ${r.created_by && r.created_by.name ? ` · ${esc(r.created_by.name)}` : ''}${r.status === 'done' ? ' · done' : ''}</a>`).join('')}
      </div></div>`;
    content.querySelectorAll('[data-previous] a').forEach(a => a.addEventListener('click', (e) => {
      e.preventDefault(); modal.close(); router.navigate(a.getAttribute('href'));
    }));
  }).catch(() => { /* the list is a convenience; the sheet works without it */ });

  let busy = false;
  async function create(close) {
    if (busy) return;
    const whenVal = content.querySelector('#mp-when').value;
    const body = {
      entity: subject.entity, meeting_kind: state.kind,
      meeting_at: whenVal ? localInputToISO(whenVal) : undefined,
      attendees: who.value.split(',').map(x => x.trim()).filter(Boolean),
      objective: content.querySelector('#mp-aim').value.trim() || undefined,
    };
    if (subject.leadId) body.lead_id = subject.leadId; else body.customer_code = subject.code;
    const deal = content.querySelector('#mp-deal');
    if (deal && deal.value) body.work_item_id = deal.value;
    busy = true; saving(modal, true, 'Write the plan');
    try {
      const res = await salesApi.createMeetingPlan(body);
      close();
      router.navigate(`/meeting-plans/${res.data.id}?write=1`);
    } catch (err) {
      showError(err.message || 'The plan could not be started.');
      saving(modal, false, 'Write the plan');
    } finally { busy = false; }
  }
  return modal;
}
